import { c as _c } from "react/compiler-runtime";
import type { InvestigationAgentContext } from '@onefootprint/request-types/dashboard';
import { LoadingSpinner } from '@onefootprint/ui';
import RuleTraceRow from '@/components/entity-onboarding-details/components/obe-rules/components/rule-trace-row';
import useAgentRulesNode from '../../../hooks/use-agent-rules-node';
import JsonView from './json-view';

// The artifact persists only the winner (`fired`) and whether the default was used; the group values
// and condition expressions live in the agent graph, which is hydrated below.
type RuleResult = {
  used_default?: boolean;
  fired?: {
    group_id?: string;
  } | null;
};
const parseRuleResult = (content: string): RuleResult | null => {
  try {
    const parsed: unknown = JSON.parse(content);
    return parsed && typeof parsed === 'object' ? parsed as RuleResult : null;
  } catch {
    return null;
  }
};
type RuleResultViewProps = {
  content: string;
  agent?: InvestigationAgentContext;
  nodeId?: string;
};

// Renders the rule the way it reads: each group as `if … → then <value>`, first-matching group
// highlighted, `Otherwise → <default>` — reusing the case-review RuleTraceRow. Falls back to raw JSON
// when the rule definition can't be hydrated (no agent/node context).
const RuleResultView = t0 => {
  const $ = _c(30);
  const {
    content,
    agent,
    nodeId
  } = t0;
  const t1 = agent?.id ?? "";
  const t2 = agent?.versionId ?? "";
  const t3 = nodeId ?? "";
  let t4;
  if ($[0] !== t1 || $[1] !== t2 || $[2] !== t3) {
    t4 = {
      agentId: t1,
      versionId: t2,
      nodeId: t3
    };
    $[0] = t1;
    $[1] = t2;
    $[2] = t3;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const {
    node,
    isPending
  } = useAgentRulesNode(t4);
  let t5;
  if ($[4] !== content) {
    t5 = parseRuleResult(content);
    $[4] = content;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const result = t5;
  if (isPending) {
    let t6;
    if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = <div className="flex items-center justify-center py-6"><LoadingSpinner /></div>;
      $[6] = t6;
    } else {
      t6 = $[6];
    }
    return t6;
  }
  if (!result || !node) {
    let t6;
    if ($[7] !== content) {
      t6 = <JsonView content={content} />;
      $[7] = content;
      $[8] = t6;
    } else {
      t6 = $[8];
    }
    return t6;
  }
  const {
    used_default,
    fired
  } = result;
  let t6;
  if ($[9] !== node.key) {
    t6 = value => <span className="text-primary">{node.key ? `outputs.${node.key} = ${value}` : value}</span>;
    $[9] = node.key;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const outcome = t6;
  let t7;
  if ($[11] !== fired?.group_id || $[12] !== node.groups || $[13] !== outcome || $[14] !== used_default) {
    let t8;
    if ($[16] !== fired?.group_id || $[17] !== outcome || $[18] !== used_default) {
      t8 = (group, index) => <RuleTraceRow key={group.id} triggered={!used_default && fired?.group_id === group.id} conditions={group.conditions.map(_temp)} noTriggerLabel="" outcome={outcome(group.value)} isFirstEntry={index === 0} isLastEntry={false} />;
      $[16] = fired?.group_id;
      $[17] = outcome;
      $[18] = used_default;
      $[19] = t8;
    } else {
      t8 = $[19];
    }
    t7 = node.groups.map(t8);
    $[11] = fired?.group_id;
    $[12] = node.groups;
    $[13] = outcome;
    $[14] = used_default;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const t8 = !!used_default;
  let t9;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = [];
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== node.defaultValue || $[22] !== outcome) {
    t10 = outcome(node.defaultValue);
    $[21] = node.defaultValue;
    $[22] = outcome;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== t10 || $[25] !== t8) {
    t11 = <RuleTraceRow triggered={t8} conditions={t9} noTriggerLabel="Otherwise" outcome={t10} isFirstEntry={false} isLastEntry={true} />;
    $[24] = t10;
    $[25] = t8;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] !== t11 || $[28] !== t7) {
    t12 = <div className="flex flex-col">{t7}{t11}</div>;
    $[27] = t11;
    $[28] = t7;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  return t12;
};
export default RuleResultView;
function _temp(condition) {
  return condition.expression;
}